/**
 * Plan-level Decisions on an item's Decision Trail — shared, dependency-free.
 *
 * Used by the in-app Trail (src/lib/decisionTrailService.ts) and the public
 * `GET /v1/items/:id/decision-lineage` route, so the two cannot drift.
 *
 * Some Decisions are recorded on a plan, not on an item: authorize_por
 * (mig 788) attaches its `plan_of_record_authorized` Decision to the plan
 * cycle / version / record / snapshot via related_objects. They reach an item
 * through the plans that placed it: plan_records whose planning_priority_id
 * is the item (above or below the line; the engine's priority grain uses the
 * item id, mig 764) and initiatives for the item (included commitments).
 */

import { readStoredPlanDecision } from './storedPlanDecisions.ts';

type Row = Record<string, unknown>;

export type PlanAttachKind = 'plan_version' | 'plan_cycle';

/**
 * Decision sources recorded on a plan, and the related_objects kind that ties
 * each one to a plan containing the item:
 *
 *   plan_of_record_authorized — matched by plan VERSION, so an item dropped
 *     from a later version is not credited with that version's POR.
 *   plan_cycle_activated — activating a plan cycle (owner ruling; the DB write
 *     is a separate change). Attached to the plan cycle, so matched by CYCLE.
 *     Harmless until those rows exist.
 */
export const PLAN_DECISION_SOURCES: Readonly<Record<string, ReadonlyArray<PlanAttachKind>>> = {
  plan_of_record_authorized: ['plan_version'],
  plan_cycle_activated: ['plan_cycle'],
};

function str(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

export function isPlanDecisionSource(source: unknown): boolean {
  const s = str(source);
  return s !== null && Object.prototype.hasOwnProperty.call(PLAN_DECISION_SOURCES, s);
}

/**
 * Normalise the item's placement rows into plan_version_id + decision.
 * plan_records carry `decision` ("Included" / "Excluded"); an initiative exists
 * only for an included commitment.
 */
export function planPlacementsFrom(
  planRecords: ReadonlyArray<Row>,
  initiatives: ReadonlyArray<Row>,
): Row[] {
  return [
    ...planRecords.map((r) => ({
      plan_version_id: r.plan_version_id,
      // Rows written before a decision was renamed keep the old text; read them as today's word.
      decision: readStoredPlanDecision(typeof r.decision === 'string' ? r.decision : null),
    })),
    ...initiatives.map((i) => ({ plan_version_id: i.plan_version_id, decision: 'Included' })),
  ];
}

/** Distinct plan version ids from placement rows. */
export function planVersionIdsOf(planPlacements: ReadonlyArray<Row>): string[] {
  const out = new Set<string>();
  for (const p of planPlacements) {
    const v = str(p.plan_version_id);
    if (v) out.add(v);
  }
  return [...out];
}

export interface PlanDecisionMatch {
  decision: Row;
  /** The plan version through which the Decision concerns the item. */
  planVersionId: string | null;
  /** The item's plan-record decision in that version, when known. */
  placement: string | null;
}

/**
 * The plan-level Decisions that concern this item: those whose related_objects
 * name a plan version (or, per source, a plan cycle) the item was placed in.
 */
export function matchPlanDecisionsToItem(
  planDecisions: ReadonlyArray<Row>,
  planPlacements: ReadonlyArray<Row>,
  planVersions: ReadonlyArray<Row>,
): PlanDecisionMatch[] {
  const placementByVersion = new Map<string, string | null>();
  for (const p of planPlacements) {
    const v = str(p.plan_version_id);
    if (!v) continue;
    const prior = placementByVersion.get(v) ?? null;
    // An "Included" record wins over any other record for the same version.
    if (prior === 'Included') continue;
    placementByVersion.set(v, str(p.decision) ?? prior);
  }
  const versionByCycle = new Map<string, string>();
  for (const pv of planVersions) {
    const id = str(pv.id);
    const cycle = str(pv.plan_cycle_id);
    if (id && cycle && placementByVersion.has(id) && !versionByCycle.has(cycle)) versionByCycle.set(cycle, id);
  }

  const out: PlanDecisionMatch[] = [];
  for (const d of planDecisions) {
    const kinds = PLAN_DECISION_SOURCES[str(d.source) ?? ''];
    if (!kinds || !Array.isArray(d.related_objects)) continue;
    let matchedVersion: string | null | undefined;
    for (const ref of d.related_objects as unknown[]) {
      if (!ref || typeof ref !== 'object') continue;
      const kind = str((ref as Row).kind);
      const id = str((ref as Row).id);
      if (!kind || !id || !kinds.includes(kind as PlanAttachKind)) continue;
      if (kind === 'plan_version' && placementByVersion.has(id)) { matchedVersion = id; break; }
      if (kind === 'plan_cycle' && versionByCycle.has(id)) { matchedVersion = versionByCycle.get(id) ?? null; break; }
    }
    if (matchedVersion === undefined) continue;
    out.push({
      decision: d,
      planVersionId: matchedVersion,
      placement: matchedVersion ? placementByVersion.get(matchedVersion) ?? null : null,
    });
  }
  return out;
}
